import { Reveal } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";

const CONTENT = {
  aftercare: {
    title: "Aftercare",
    eyebrow: "Heal it right",
    intro: "A great tattoo is half the work — the other half is how you heal it. Follow these steps closely for the cleanest result.",
    sections: [
      { h: "First 24 hours", b: "Leave the initial bandage on as directed by your artist. When you remove it, wash your hands, then gently clean the tattoo with lukewarm water and a mild, fragrance-free soap. Pat dry with a clean paper towel — never rub." },
      { h: "Days 2–14", b: "Apply a thin layer of recommended aftercare ointment or unscented lotion 2–3 times a day. Keep it clean and moisturized, not smothered. Some peeling and light flaking is normal — do not pick or scratch." },
      { h: "Avoid", b: "No soaking: skip pools, hot tubs, baths and the ocean until fully healed. Keep the tattoo out of direct sunlight and avoid tight, abrasive clothing over the area." },
      { h: "Long term", b: "Once healed, sunscreen is your tattoo's best friend. UV exposure is the fastest way to fade line and contrast. Protect it and it will read clean for decades." },
      { h: "If something's off", b: "Excessive redness, swelling, heat or discharge beyond the first couple of days warrants a call to the studio and, if needed, a medical professional." },
    ],
  },
  policies: {
    title: "Studio Policies",
    eyebrow: "The house rules",
    intro: "Clear expectations make for a better experience for everyone in the chair.",
    sections: [
      { h: "Deposits", b: "A non-refundable deposit is required to book. It is applied to the final cost of your tattoo. Deposits may be transferred once with at least 48 hours' notice before your appointment." },
      { h: "Rescheduling & cancellations", b: "Please give at least 48 hours' notice to reschedule. No-shows and late cancellations forfeit the deposit." },
      { h: "Age & ID", b: "All clients must be 18 or older and present a valid government-issued photo ID. We do not tattoo minors under any circumstances." },
      { h: "Health & safety", b: "We operate a fully licensed, sterilized studio. Please disclose relevant medical conditions. Do not arrive intoxicated — we reserve the right to refuse service." },
      { h: "Guests", b: "For comfort and safety, please limit guests. Children cannot be present during tattooing." },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    eyebrow: "Your data",
    intro: "We respect your privacy. This policy explains what we collect and why.",
    sections: [
      { h: "What we collect", b: "When you submit a booking or contact request we collect your name, email, phone number, tattoo details and any reference images you upload. This information is used solely to respond to and manage your request." },
      { h: "How we use it", b: "Your information is used to communicate about your appointment, process your booking and improve our service. We do not sell your personal data." },
      { h: "Storage & security", b: "Booking data is stored securely and access is restricted to studio staff. Reference images are stored in secure object storage and are not published publicly." },
      { h: "Your rights", b: "You may request access to, correction of, or deletion of your personal information at any time by contacting the studio." },
    ],
  },
  terms: {
    title: "Terms of Service",
    eyebrow: "The fine print",
    intro: "By using this website and booking with Obsidian Art & Tattoo Studio you agree to the following terms.",
    sections: [
      { h: "Booking requests", b: "Submitting a request does not guarantee an appointment. All bookings are confirmed by the studio after review of your idea, artist availability and deposit." },
      { h: "Artwork & likeness", b: "Custom designs remain the artistic work of the artist. The studio may photograph completed tattoos for portfolio and promotional use unless you request otherwise in writing." },
      { h: "Liability", b: "Clients are responsible for disclosing allergies and medical conditions and for following aftercare instructions. The studio is not liable for outcomes resulting from improper aftercare." },
      { h: "Changes", b: "We may update these terms and our policies from time to time. Continued use of the site constitutes acceptance of the current terms." },
    ],
  },
};

export default function LegalPage({ page }) {
  const c = CONTENT[page];
  if (!c) return null;
  return (
    <>
      <Seo title={c.title} description={`${c.title} — Obsidian Art & Tattoo Studio, Springfield VA.`} />
      <section className="pt-36 lg:pt-44 pb-14 max-w-[1000px] mx-auto px-6 sm:px-10 lg:px-16 border-b border-ash">
        <Reveal><p className="eyebrow mb-6">{c.eyebrow}</p></Reveal>
        <Reveal delay={60}><h1 className="display text-5xl sm:text-6xl lg:text-7xl text-white">{c.title}.</h1></Reveal>
        <Reveal delay={120}><p className="mt-8 max-w-2xl text-[#9E9EA8] text-lg leading-relaxed">{c.intro}</p></Reveal>
      </section>
      <section className="max-w-[1000px] mx-auto px-6 sm:px-10 lg:px-16 py-16">
        <div className="divide-y divide-ash border-y border-ash">
          {c.sections.map((s, i) => (
            <Reveal key={i}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-10">
                <h2 className="font-serif text-3xl text-white md:col-span-1">{s.h}</h2>
                <p className="text-[#9E9EA8] leading-relaxed md:col-span-2">{s.b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
